import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AvatarBadge } from "@/components/AvatarBadge";
import { RevealLines, FadeUp } from "@/components/Reveal";
import { ThoughtForm, MOODS } from "@/components/ThoughtForm";

const ago = (iso) => {
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return ""; }
};

export const ThoughtCard = ({ t, mine, onDelete, i }) => (
  <motion.article layout initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 0.8, delay: (i % 3) * 0.08 }} data-testid={`thought-card-${t.id}`}
    className="break-inside-avoid mb-5 rounded-[20px] border border-purple-300/20 bg-[#170a24]/75 backdrop-blur-xl p-6 hover:border-purple-200/50 transition-colors">
    <div className="flex items-center justify-between">
      <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-200">{t.mood}</span>
      {mine && (
        <button onClick={() => onDelete(t.id)} aria-label="Delete thought" data-testid={`thought-delete-${t.id}`} className="text-purple-300 hover:text-white">
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
    <p className="mt-4 font-display text-2xl leading-snug text-purple-50 whitespace-pre-line">{t.text}</p>
    <div className="mt-5 flex items-center gap-3 text-sm text-purple-200">
      {t.author_avatar ? <AvatarBadge avatar={t.author_avatar} size={28} testid={`thought-avatar-${t.id}`} /> :
        t.author_picture ? <img src={t.author_picture} alt="" className="w-7 h-7 rounded-full" /> :
        <span className="w-7 h-7 rounded-full bg-purple-400/30 grid place-items-center">{t.author_name?.[0]}</span>}
      <span>{t.author_name}</span><span className="text-purple-300">·</span><span>{ago(t.created_at)}</span>
    </div>
  </motion.article>
);

export const Thoughts = () => {
  const { user } = useAuth();
  const [thoughts, setThoughts] = useState([]);
  const [filter, setFilter] = useState("All");
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  useEffect(() => {
    http.get("/thoughts")
      .then((r) => setThoughts(Array.isArray(r.data) ? r.data : []))
      .catch(() => setThoughts([]));
  }, []);

  const remove = async (id) => {
    try { await http.delete(`/thoughts/${id}`); setThoughts((p) => p.filter((t) => t.id !== id)); } catch { /* ignore */ }
  };
  const shown = filter === "All" ? thoughts : thoughts.filter((t) => t.mood === filter);

  return (
    <section id="thoughts" ref={ref} className="relative py-24 md:py-36 overflow-hidden" data-testid="thoughts-section">
      <motion.div style={{ y: bgY }} className="absolute -inset-y-[12%] inset-x-0">
        <img src="/img/galaxy.webp" alt="" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-[#0e0616]/55" />
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#0e0616] to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0e0616] to-transparent" />
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-purple-200 mb-6">III — The Thought Wall</p>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight">
              <RevealLines inView lines={[<>Leave a thought,</>, <em className="text-amber-200">take a star.</em>]} />
            </h2>
            <FadeUp className="mt-8 max-w-md text-purple-50/90 leading-relaxed">
              Write what you can't say out loud. Read what others left behind. Everyone here is a little lost, and that's the magic.
            </FadeUp>
          </div>
          <div className="lg:col-span-5 lg:pt-10"><ThoughtForm onPosted={(t) => setThoughts((p) => [t, ...p])} /></div>
        </div>
        <div className="flex flex-wrap gap-2 mb-8" data-testid="thought-filters">
          {["All", ...MOODS].map((m) => (
            <button key={m} data-testid={`thought-filter-${m.toLowerCase()}`} onClick={() => setFilter(m)}
              className={`rounded-full px-4 py-1.5 text-sm border backdrop-blur transition-colors ${filter === m ? "bg-purple-100 text-[#0e0616] border-purple-100" : "border-purple-200/40 bg-[#0e0616]/40 hover:border-purple-100"}`}>
              {m}
            </button>
          ))}
        </div>
        <div className="columns-1 md:columns-2 lg:columns-3 gap-5" data-testid="thoughts-wall-grid">
          {shown.slice(0, 6).map((t, i) => <ThoughtCard key={t.id} t={t} i={i} mine={user?.user_id === t.user_id} onDelete={remove} />)}
        </div>
        {thoughts.length > 0 && (
          <div className="mt-10 flex justify-center">
            <Link to="/thoughts" data-testid="thoughts-view-all-link"
              className="group inline-flex items-center gap-3 rounded-full border border-purple-200/40 bg-[#0e0616]/50 backdrop-blur px-7 py-3.5 hover:border-amber-200 hover:text-amber-200 transition-colors">
              Read all thoughts <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        )}
        {shown.length === 0 && (
          <p className="font-display italic text-2xl text-purple-100 text-center py-16" data-testid="thoughts-empty">The hollow is quiet… be the first to whisper.</p>
        )}
      </div>
    </section>
  );
};
